import { mediaUrl, useSiteMedia } from "../lib/siteMedia";
import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import SectionHeader from "../components/SectionHeader";

export default function About() {
  const media = useSiteMedia();
  return (
    <div data-testid="about-page">
      <section className="relative pt-32 min-h-[70vh] flex items-end px-6 sm:px-10 lg:px-16 overflow-hidden">
        <div className="absolute inset-0">
          <img src={mediaUrl(media, "about_hero")} alt="" className="w-full h-full object-cover ken-burns" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative max-w-[1600px] mx-auto pb-16 w-full">
          <p className="eyebrow">About</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-8xl mt-6 leading-[1.02] tracking-tight max-w-4xl">
            The story of <span className="italic" style={{ color: "var(--twr-bronze)" }}>The Win Room.</span>
          </h1>
        </div>
      </section>

      <section className="section">
        <div className="max-w-[1100px] mx-auto">
          <p className="font-editorial italic text-2xl sm:text-3xl leading-snug text-[var(--twr-warm-white)]/85 dropcap">
            The Win Room was born from a simple observation: the rooms where the best business is done are rarely the ones with a sign on the door. They are quiet, considered, and reserved for those who understand the long game.
          </p>
          <p className="text-[var(--twr-warm-white)]/75 mt-10 leading-relaxed text-lg">
            Set within Lake Nona's growing center of gravity, The Win Room brings together a restaurant, private bar,
            walk-in humidor, and a collection of meeting rooms designed to feel more like a private aviation lounge than
            a traditional social club. It was created for founders, executives, family principals, and high-performers
            who want a place to land — to think, to host, to entertain, and, when needed, to disappear.
          </p>
          <p className="text-[var(--twr-warm-white)]/75 mt-6 leading-relaxed text-lg">
            We took inspiration from the great rooms of the world — Soho House, Davidoff of Geneva, Aman, and the
            St. Regis King Cole Bar — and then did the most important thing: we forgot the inspiration and built for our city.
          </p>
        </div>
      </section>

      <section className="section bg-[#0c0b0a]">
        <div className="max-w-[1400px] mx-auto">
          <SectionHeader eyebrow="Our Mission" title="To make business feel like the long sit." />
          <div className="grid md:grid-cols-3 gap-6 mt-14">
            {[
              { n: "I.", t: "Discretion", c: "What is said here, stays here. The first principle of a serious club." },
              { n: "II.", t: "Craft", c: "From the kitchen to the bar to the humidor — built by hand, judged by the best." },
              { n: "III.", t: "Community", c: "We are not a venue. We are a circle. Membership is an act of belonging." },
            ].map((b) => (
              <div key={b.n} className="surface p-8">
                <p className="font-serif text-3xl text-[var(--twr-bronze)]">{b.n}</p>
                <h3 className="font-serif text-2xl mt-4">{b.t}</h3>
                <p className="text-[var(--twr-warm-white)]/65 text-sm mt-4 leading-relaxed">{b.c}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="max-w-[1500px] mx-auto grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-6 frame aspect-[4/5]">
            <img src={mediaUrl(media, "about_leadership")} alt="Members at an event" className="w-full h-full object-cover" />
          </div>
          <div className="lg:col-span-6" data-testid="about-founders-note">
            <p className="eyebrow">A Note from the Founders</p>
            <h2 className="font-serif text-4xl sm:text-5xl mt-4 leading-tight">A room for what comes next.</h2>
            <p className="text-[var(--twr-warm-white)]/70 mt-6 leading-relaxed">
              Founded by <span className="text-[var(--twr-bronze)]">Colton Elliott</span>, <span className="text-[var(--twr-bronze)]">Christobal Pinochet</span>, and <span className="text-[var(--twr-bronze)]">Bo Loudon</span>, The Win Room was created with a simple purpose: to build a private, world-class environment for the people shaping what comes next.
            </p>
            <p className="text-[var(--twr-warm-white)]/70 mt-5 leading-relaxed">
              This is not just a restaurant, a bar, or a cigar lounge. It is a room for serious conversations, meaningful
              relationships, strategic introductions, and moments that move business and life forward.
            </p>
            <p className="text-[var(--twr-warm-white)]/70 mt-5 leading-relaxed">
              The Win Room is for those who understand that access, trust, and reputation are built over time — and that
              the right room can change everything.
            </p>
            <Link to="/membership" className="btn-bronze mt-10">Become a Member <ChevronRight size={14} /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
